/**
 * Pure URL construction for the runtime noVNC tunnel WebSocket.
 *
 * Split out of `NoVncPane` so it can be unit-tested in the `node` vitest
 * env without dragging in `@novnc/novnc` (browser-only ESM) or a DOM, and
 * so the VNC tunnel's lexserver-host derivation lives in one tested place.
 * (Chat `/ws` derives the same host independently in
 * `useGateway.resolveGatewayUrl`; folding both onto a shared helper is a
 * follow-up — it returns origin-only here vs the full URL there.)
 *
 * The tunnel path produced by `getBrowserStatus()` is SPA-relative
 * (`/ws/runtime/<userId>/api/lx/openclaw/browser/vnc/ws`). It MUST be
 * anchored to the **lexserver** WS host — not the lexide SPA host — or the
 * upgrade dies at the SPA App Service edge (WS close 1006: `app.lexiun.ai`
 * serves static files only, with no `/ws/*` reverse proxy). This mirrors
 * how chat `/ws` resolves its host in `useGateway.resolveGatewayUrl()`.
 *
 * Regression context: the 2026-06-03 prod smoke failed exactly here — the
 * URL was built against `window.location.origin` (the SPA host). localdev
 * can't catch it because the Vite dev-proxy makes same-origin correct in
 * dev; `vnc-url.test.ts` pins the prod host derivation instead. See
 * ai-docs/lexruntime-noVNC-monkey-patch.md §10.
 */

/** The lexserver WebSocket origin (scheme + host) that the runtime VNC
 *  tunnel attaches to.
 *
 *  - **Prod** (`VITE_LEXSERVER_WS_URL` set, e.g. `wss://srv.lexiun.ai/ws`):
 *    take that URL's origin (`wss://srv.lexiun.ai`) — the SAME host chat
 *    `/ws` uses — so the tunnel upgrade reaches lexserver, NOT the lexide
 *    SPA host.
 *  - **Dev** (env unset): same-origin — the Vite dev-server proxy forwards
 *    `/ws/*` to the local lexserver.
 *
 *  A malformed env value falls through to same-origin rather than throwing.
 *  Mirrors `useGateway.resolveGatewayUrl()`. */
export function resolveRuntimeWsOrigin(): string {
  const fromEnv = (import.meta.env.VITE_LEXSERVER_WS_URL as string | undefined)?.trim();
  if (fromEnv) {
    try {
      const u = new URL(fromEnv);
      return `${u.protocol}//${u.host}`;
    } catch {
      // Malformed env → fall through to same-origin rather than throw.
    }
  }
  return window.location.origin;
}

/** Build the runtime VNC WebSocket URL: resolve the (already
 *  `userId`-prefixed) tunnel path against the lexserver WS origin,
 *  normalise the scheme to `ws`/`wss`, and append the short-TTL `?token=`
 *  (websockify validates it server-side). Pure given `wsOrigin`. */
export function buildVncWsUrl(
  vncEndpoint: string,
  token: string,
  wsOrigin: string = resolveRuntimeWsOrigin(),
): string {
  const u = new URL(vncEndpoint, wsOrigin);
  // The origin may arrive as http(s) (dev same-origin / page origin) or
  // already ws(s) (the env-derived lexserver origin). Normalise either to
  // the WS scheme; an already-ws(s) origin is left untouched.
  if (u.protocol === "https:") u.protocol = "wss:";
  else if (u.protocol === "http:") u.protocol = "ws:";
  u.searchParams.set("token", token);
  return u.toString();
}
